import type {
  HubCreateProject,
  HubJob,
  HubJobDependencyRefs,
  HubJobKind,
  HubProjectDatapool,
  HubStartJob,
  HubUpdateProject,
  RequestParams,
} from '@robovision/hub'
import { ImportExportRecordImportMode } from '@robovision/import-export'
import {
  addOrReplaceBy,
  HttpStatusCode,
  subtract,
} from '@robovision/rvai-utils'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import { ApiErrorId } from '~domain/ApiErrorId'
import {
  CredibilityScore,
  CredibilityScoreThreshold,
} from '~domain/CredibilityScore'
import type { Datapool } from '~domain/datapool/Datapool'
import { Exceptions } from '~domain/Exceptions'
import type { SelectedFilters } from '~domain/filtering/SelectedFilters'
import type { ImportJob } from '~domain/io/ImportJob'
import type {
  PredictionJobData,
  TrainingJobData,
} from '~domain/projects/JobData'
import { Project } from '~domain/projects/Project'
import { i18n } from '~i18n'
import { hubApi } from '~services/api'
import { acceptStatus } from '~utils/api'
import { filtersToQuery } from '~utils/filters'
import {
  buildJobDependencies,
  buildRuntimeParameters,
} from './helpers'
import { useModels } from '../composables/useModels'
import { useAlgorithmsStore } from '../useAlgorithmsStore'
import { useDatapoolsStore } from '../useDatapoolsStore'
import { useImportJobsStore } from '../useImportJobsStore'
import { useWorkflowsStore } from '../useWorkflowsStore'


interface AddData {
  createDatapoolIfMissing?: boolean,
  files: File[],
  id: string,
  importMode?: ImportExportRecordImportMode,
  setEventSource?: boolean,
}

export const useProjectsStore = defineStore('projects', () => {
  const state = reactive({
    datapools: new Map<string, string[]>(),
    deletingProjectsIds: [] as string[],
    importJobs: new Map<string, string[]>(),
    loading: false as boolean,
    projects: [] as Project[],
    thumbnails: new Map<string, Promise<Blob | undefined>>(),
  })

  async function getExampleDefinitionId (project: Project): Promise<string> {
    const { data: definitions } = await hubApi.projects.getDefaultProjectExampleDefinitions(project.id)

    if (!definitions.length) throw new Error('Algorithm contains no example definition.')
    if (definitions.length > 1) throw new Error(`Multiple example definitions per project are not supported. ${definitions.map(def => def.id).join(', ')}.`)

    const [exampleDefinition] = definitions
    return exampleDefinition?.id ?? ''
  }

  const getters = {
    canTest (id: string): boolean {
      const { getAlgorithmDefinition } = useAlgorithmsStore()

      const project = this.getProject(id)

      if (!project) throw new Error(`Unknown project ${id}`)
      if (!project.defaultAlgorithmId) throw new Error(`Project ${id} does not have a default algorithm`)
      if (!project.defaultAlgorithmVersion) throw new Error(`Project ${id} does not have a default algorithm`)

      const algorithmDefinition = getAlgorithmDefinition(project.defaultAlgorithmId, project.defaultAlgorithmVersion)
      if (!algorithmDefinition) return false

      return !!algorithmDefinition.jobs.annotationPrediction
    },

    getAlgorithmNameWithVersion (project: Project): string {
      if (!project.defaultAlgorithmId || !project.defaultAlgorithmVersion) return ''
      const { getAlgorithm } = useAlgorithmsStore()
      const algorithm = getAlgorithm(project.defaultAlgorithmId, project.defaultAlgorithmVersion)
      const algorithmName = algorithm?.name ?? project.normalizedType
      return `${algorithmName} ${project.defaultAlgorithmVersion ?? ''}`
    },

    getAlgorithmNameWithVersionByProjectId (projectId: string): string {
      const project = this.getProject(projectId)
      if (!project?.defaultAlgorithmId || !project?.defaultAlgorithmVersion) return ''
      const { getAlgorithm } = useAlgorithmsStore()
      const algorithm = getAlgorithm(project.defaultAlgorithmId, project.defaultAlgorithmVersion)
      const algorithmName = algorithm?.name ?? project.normalizedType
      return `${algorithmName} ${project.defaultAlgorithmVersion ?? ''}`
    },

    getAlgorithmType (project: Project): string {
      if (!project.defaultAlgorithmId || !project.defaultAlgorithmVersion) return ''
      const { getAlgorithm } = useAlgorithmsStore()
      const algorithm = getAlgorithm(project.defaultAlgorithmId, project.defaultAlgorithmVersion)
      return algorithm?.type ?? ''
    },

    getProject (id: string): Project | undefined {
      return state.projects.find(v => v.id === id)
    },

    async getProjectByDatapoolId (datapoolId: string): Promise<Project | undefined> {
      try {
        const projectId = await this.getProjectIdByDatapoolId(datapoolId)
        if (!projectId) return
        return this.getProject(projectId) ?? await actions.checkProjectExists(projectId)
      } catch (e) {
        console.log(e)
      }
    },

    getProjectByName (name: string): Project | undefined {
      return state.projects.find(v => v.name.toLocaleLowerCase() === name?.toLocaleLowerCase())
    },

    async getProjectDatapoolId (projectId: string): Promise<string> {
      const [datapoolId] = this.getProjectDatapools(projectId)
      if (datapoolId) return datapoolId

      const [datapool] = await actions.loadProjectDatapools(projectId)
      return datapool?.id ?? ''
    },

    getProjectDatapools (projectId: string): string[] {
      return state.datapools.get(projectId) ?? []
    },

    getProjectDeletingProcess (id: string): boolean {
      return state.deletingProjectsIds.includes(id)
    },

    async getProjectIdByDatapoolId (datapoolId: string): Promise<string | undefined> {
      try {
        const { getDatapool } = useDatapoolsStore()
        const datapool = await getDatapool(datapoolId)
        return datapool?.projectId
      } catch (e) {
        console.log(e)
      }
    },

    getProjectImportJobs (id: string): ImportJob[] {
      const { imports } = useImportJobsStore()

      const importIds = state.importJobs.get(id) ?? []
      return importIds
        .map(importId => imports.get(importId))
        .filter(Boolean)
    },

    getProjectName (id: string): string {
      return this.getProject(id)?.name || ''
    },

    getProjects (): Project[] {
      return state.projects
    },

    getProjectType (id: string): string {
      const project = this.getProject(id)
      return project ? this.getAlgorithmType(project) : ''
    },

    hasAnnotatedSamples (id: string): boolean {
      const {
        datapools,
        metadata,
      } = useDatapoolsStore()
      const projectDatapoolIds = state.datapools.get(id)
      const datapool = !!projectDatapoolIds?.length && datapools.find(v => projectDatapoolIds?.includes(v.id))
      const datapoolMetadata = datapool && metadata.get(datapool.id)

      return !!(datapoolMetadata && datapoolMetadata?.annotatedSampleCount > 0)
    },

    hasAnnotatedSamplesForTraining (id: string): boolean {
      const {
        datapools,
        getDatapoolAnnotatedSamplesCount,
      } = useDatapoolsStore()
      const projectDatapoolIds = state.datapools.get(id)
      const datapool = !!projectDatapoolIds?.length && datapools.find(v => projectDatapoolIds?.includes(v.id))
      if (!datapool) return false

      return !!getDatapoolAnnotatedSamplesCount(datapool.id)
    },

    isProjectReadOnly (project: Project): boolean {
      const {
        isAlgorithmAvailable,
        isAlgorithmsLoaded,
      } = useAlgorithmsStore()
      return isAlgorithmsLoaded && !isAlgorithmAvailable(
        project.defaultAlgorithmId ?? '',
        project.defaultAlgorithmVersion ?? '',
      )
    },
  }

  const actions = {
    async addData ({
      createDatapoolIfMissing = true,
      files,
      id,
      importMode = ImportExportRecordImportMode.ANY,
      setEventSource = false,
    }: AddData): Promise<string[]> {
      const { uploadData } = useDatapoolsStore()

      const project = state.projects.find(p => p.id === id)

      if (!project) throw new Error(`Unknown project ${id}`)

      await this.loadProjectDatapools(id)

      // TODO this is just getting the first datapool
      // which will not work if there are multiple example
      // definitions per algorithm
      let datapoolId = state.datapools.get(id)?.[0]
      if (!datapoolId && createDatapoolIfMissing) {
        const projectDatapool = await this.createDatapool(project)

        if (!projectDatapool) throw new Error('Unknown datapool')
        datapoolId = projectDatapool.id

        // reload the datapools as we just created one
        await this.loadProjectDatapools(id)
      } else if (!datapoolId) {
        throw new Error('Project has no associated datapools')
      }


      const importIds = await uploadData({
        files,
        id: datapoolId,
        importMode,
        setEventSource,
      })
      state.importJobs.set(id, [
        ...(state.importJobs.get(id) ?? []),
        ...importIds,
      ])

      return importIds
    },

    async checkProjectDeletable (projectId: string): Promise<{ error?: string, isDeletable: boolean }> {
      try {
        // The `dryRun` query parameter is not exposed to the OpenAPI schema because it is our internal parameter.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await hubApi.projects.deleteProject(projectId, { query: { dryRun: true } })

        return { isDeletable: true }
      } catch (e) {
        const errorData = e instanceof AxiosError ? e.response?.data : undefined

        const error = (): string => {
          switch (errorData?.id) {
            case 'ResourceInUseError':
              return i18n.t('projects.table.disableDeleteProjectResourceInUse', { resource_name: errorData?.parameters?.resource_name })
            case 'ResourcesInUseError':
              return i18n.t('projects.table.disableDeleteProjectResourceInUse', { resource_name: errorData?.parameters?.resource_names })
            default:
              return i18n.t('projects.table.disableDeleteProject')
          }
        }

        return {
          error: error(),
          isDeletable: false,
        }
      }
    },

    async checkProjectExists (id: string): Promise<Project | undefined> {
      const response = await hubApi.projects.getProject(
        id,
        { validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND) },
      )
      if (!response?.data || response.data.id === ApiErrorId.ProjectIdNotFound) return undefined

      return Project.fromResponse(response.data)
    },

    async createDatapool (project: Project): Promise<Datapool | undefined> {
      const { loadDatapool } = useDatapoolsStore()

      const exampleDefinitionId = await getExampleDefinitionId(project)
      const { data: projectDatapool } = await hubApi.datapools.createDatapool({
        description: `Definition ${exampleDefinitionId}`,
        exampleDefinitionId,
        name: `Datapool for project ${project.name}`,
        projectId: project.id,
      })

      const datapool = await loadDatapool(projectDatapool.id)
      return datapool
    },

    async createProject (data: HubCreateProject) {
      const { data: projectData } = await hubApi.projects.createProject(data)

      const project = Project.fromResponse(projectData)

      state.projects.unshift(project)

      return project
    },

    async deleteDataFromProject (projectId: string, datapoolId: string) {
      const { deleteDatapoolData } = useDatapoolsStore()

      try {
        state.loading = true
        await deleteDatapoolData(datapoolId)
        state.importJobs.delete(projectId)
      } finally {
        state.loading = false
      }
    },

    async deleteProjects (projects: Project[]) {
      try {
        state.loading = true
        state.deletingProjectsIds.push(...projects.map(project => project.id))

        const ids = projects.map(v => v.id)
        await Promise.all(ids.map(v => hubApi.projects.deleteProject(v)))

        state.projects = state.projects.filter(v => !ids.includes(v.id))
        ids.forEach((v) => {
          state.datapools.delete(v)
        })
      } finally {
        state.loading = false
        state.deletingProjectsIds = subtract(state.deletingProjectsIds, projects.map(project => project.id))
      }
    },

    async deleteProjectSamplesByImportJob (projectId: string, job: ImportJob) {
      const { deleteSampleByImportJob } = useDatapoolsStore()

      const datapoolId = state.datapools.get(projectId)?.[0]
      if (!datapoolId) return

      await deleteSampleByImportJob(projectId, datapoolId, job)

      const newImportJobs = state.importJobs.get(projectId)?.filter(jobId => jobId !== job.importId) ?? []
      state.importJobs.set(projectId, newImportJobs)
    },

    async getProjectThumbnail (project: Project): Promise<Blob | undefined> {
      if (!state.thumbnails.has(project.id)) {
        const promise = new Promise<Blob | undefined>(resolve => {
          hubApi.projects.getProjectThumbnail(
            project.id,
          ).then(({ data }) => {
            resolve(new Blob([data]))
          }).catch(e => {
            console.error(e)
            resolve(undefined)
          })
        })
        state.thumbnails.set(project.id, promise)
        return promise
      }

      return state.thumbnails.get(project.id)
    },

    async isProjectNameUnique (projectName: string, currentProjectName?: string): Promise<boolean> {
      // If new value is the same as the current value no need to check
      if (currentProjectName && projectName.toLowerCase() === currentProjectName.toLowerCase()) return true

      try {
        const {
          data,
          status,
        } = await hubApi.projects.isProjectNameAvailable(
          encodeURIComponent(projectName),
          { validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND) },
        )
        if (status !== HttpStatusCode.OK) return false
        return data.available
      } catch {
        return false
      }
    },

    async loadMetadataValues (): Promise<Record<string, string[]>> {
      try {
        const { data } = await hubApi.projects.listProjectMetadata()
        return data
      } catch (e) {
        console.error(e)
        throw e
      }
    },

    async loadProjectById (id: string, force: boolean = true): Promise<Project> {
      const existingProject = getters.getProject(id)
      if (!force && existingProject) return existingProject

      const {
        data,
        status,
      } = await hubApi.projects.getProject(
        id,
        { validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND) },
      )

      if (status === HttpStatusCode.NOT_FOUND) {
        throw new Exceptions.ProjectNotFound()
      }

      const project = Project.fromResponse(data)

      state.projects = addOrReplaceBy(
        state.projects,
        project,
        v => v.id === project.id,
      )

      return project
    },

    async loadProjectByName (name: string, force: boolean = true): Promise<Project> {
      const existingProject = getters.getProjectByName(name)
      if (!force && existingProject) return existingProject

      const { data } = await hubApi.projects.getProjectByName(name)
      const project = Project.fromResponse(data)

      state.projects = addOrReplaceBy(
        state.projects,
        project,
        v => v.id === project.id,
      )

      return project
    },

    async loadProjectDatapools (id: string): Promise<HubProjectDatapool[]> {
      const {
        loadDatapool,
        loadDatapoolAnnotatedSamplesCount,
        loadDatapoolMetadata,
      } = useDatapoolsStore()

      const { data: datapools } = await hubApi.datapools.listDatapools({ projectId: id })

      // TODO add single api
      for (const pool of datapools.items) {
        await loadDatapool(pool.id)
        await loadDatapoolMetadata(pool.id)
        await loadDatapoolAnnotatedSamplesCount({
          id: pool.id,
          minimumAnnotationCredibility: CredibilityScore.ModelAnnotation,
        })
      }

      state.datapools.set(id, datapools.items.map(d => d.id))

      return datapools.items
    },

    async loadProjectJobs (id: string, limit?: number): Promise<void> {
      const { loadImportJobs } = useImportJobsStore()

      const datapoolId = state.datapools.get(id)?.[0]
      if (!datapoolId) return

      const importIds = await loadImportJobs(datapoolId, limit)

      state.importJobs.set(id, importIds)
    },

    async loadProjects (): Promise<void> {
      const { data: projects } = await hubApi.projects.listProjects()

      state.projects = projects.items.map(Project.fromResponse)
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    async startJob (job: HubStartJob, params?: RequestParams): Promise<HubJob> {
      const { data } = await hubApi.jobs.startJob(job, params)

      return data
    },

    async startPrediction ({
      filters,
      id,
      jobData,
      kind,
    }: {
      filters?: SelectedFilters[],
      id: string,
      jobData: PredictionJobData,
      kind: HubJobKind
    }) {
      const { getAlgorithmDefinition } = useAlgorithmsStore()
      const { getModelResource } = useModels()
      const { startWorkflow } = useWorkflowsStore()

      const project = state.projects.find(p => p.id === id)
      if (!project) throw new Error(`Unknown project ${id}`)
      if (!project.defaultAlgorithmId) throw new Error(`Project ${id} does not have a default algorithm`)
      if (!project.defaultAlgorithmVersion) throw new Error(`Project ${id} does not have a default algorithm`)

      const algorithmDefinition = getAlgorithmDefinition(project.defaultAlgorithmId, project.defaultAlgorithmVersion)
      if (!algorithmDefinition) {
        throw new Error(`Unknown algorithm ${project.defaultAlgorithmId} version ${project.defaultAlgorithmVersion}`)
      }
      const predictJob = algorithmDefinition.jobs.annotationPrediction
      if (!predictJob) {
        throw new Error('Missing annotationPrediction job definition')
      }

      const [projectDatapool] = await this.loadProjectDatapools(project.id)
      if (!projectDatapool) {
        throw new Error('Project has no datapools')
      }

      const jobDefinition = algorithmDefinition.jobs[kind]
      if (!jobDefinition) {
        throw new Error(
          `Unknown job ${kind} in algorithm ${project.defaultAlgorithmId} version ${project.defaultAlgorithmVersion}`,
        )
      }
      const datapoolDependency = Object.values(jobDefinition.datapoolDependencies)
      if (datapoolDependency.length !== 1 || !datapoolDependency[0]) {
        throw new Error('annotationPrediction doesn\'t have exactly 1 datapool dependency')
      }
      const annotationArtifacts = Object.keys(jobDefinition.annotationArtifacts)
      if (annotationArtifacts.length !== 1) {
        throw new Error('annotationPrediction doesn\'t have exactly 1 annotation artifact')
      }

      const sampleFilters = filters && filters.map(filtersToQuery)
      const model = jobData.modelId ? getModelResource(jobData.modelId) : undefined
      const dependencies: HubJobDependencyRefs = await buildJobDependencies({
        jobData: {
          ...jobData,
          datapools: {
            [datapoolDependency[0].definitionId]: {
              [datapoolDependency[0].dependencyId]: {
                poolId: projectDatapool.id,
                ...(sampleFilters && {
                  sampleFilters: {
                    filters: sampleFilters,
                    minimumAnnotationCredibility: CredibilityScoreThreshold.LabelCenter,
                  },
                }),
              },
            },
          },
        },
        jobDefinition,
        model,
        projectId: project.id,
      })

      const predictionTaskData = {
        algorithmId: algorithmDefinition.id,
        algorithmVersion: algorithmDefinition.version,
        artifacts: { },
        credibility: CredibilityScore.ModelAnnotation,
        definitionId: predictJob.id,
        dependencies,
        kind: predictJob.kind,
        name: jobData.name,
        runtimeParameters: buildRuntimeParameters(jobData),
      }

      await startWorkflow({
        name: jobData.name,
        projectId: id,
        tags: ['prediction'],
        tasks: [predictionTaskData],
      })
    },

    async startTraining (id: string, kind: HubJobKind, jobData: TrainingJobData) {
      const { getAlgorithmDefinition } = useAlgorithmsStore()
      const { getModelResource } = useModels()

      const project = state.projects.find(p => p.id === id)
      if (!project) throw new Error(`Unknown project ${id}`)
      if (!project.defaultAlgorithmId) throw new Error(`Project ${id} does not have a default algorithm`)
      if (!project.defaultAlgorithmVersion) throw new Error(`Project ${id} does not have a default algorithm`)

      const algorithmDefinition = getAlgorithmDefinition(project.defaultAlgorithmId, project.defaultAlgorithmVersion)
      if (!algorithmDefinition) {
        throw new Error(`Unknown algorithm ${project.defaultAlgorithmId} version ${project.defaultAlgorithmVersion}`)
      }

      const jobDefinition = algorithmDefinition.jobs[kind]
      if (!jobDefinition) {
        throw new Error(
          `Unknown job ${kind} in algorithm ${project.defaultAlgorithmId} version ${project.defaultAlgorithmVersion}`,
        )
      }

      const model = jobData.modelId ? getModelResource(jobData.modelId) : undefined
      const dependencies: HubJobDependencyRefs = await buildJobDependencies({
        jobData,
        jobDefinition,
        model,
        projectId: project.id,
      })

      const startJobData: HubStartJob = {
        definitionId: jobDefinition.id,
        dependencies,
        name: jobData.name,
        projectId: id,
        runtimeParameters: buildRuntimeParameters(jobData),
      }

      await this.startJob(startJobData)
    },

    async updateProject (id: string, data: HubUpdateProject) {
      const { data: projectData } = await hubApi.projects.updateProject(id, data)
      const project = Project.fromResponse(projectData)

      state.projects = addOrReplaceBy(
        state.projects,
        project,
        v => v.id === project.id,
      )

      return project
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...getters,
    ...actions,
  }
})
